/* /account/inbox - the student's application email address and the admissions emails sent to it, sorted. */
import { useRef, useState } from 'react';
import { WEB_ROUTES, type Application, type InboxEvent, type InboxResponse, type RawEmail } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { Link } from '../app/nav';
import { useMe } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Icon } from '../app/icons';
import { Button, Chip, CopyButton, ExtLink, LoadError, Loading, PageHeader, Section, type Tone } from '../app/ui';
import { api, ApiError } from '../lib/api';
import { formatDate, formatDateTime, safeHttpUrl } from '../lib/format';

const DECISION_TONE: Record<NonNullable<InboxEvent['decision']>, Tone> = { admitted: 'green', denied: 'gray', waitlisted: 'orange', deferred: 'orange' };

export function Inbox() {
  const { t } = useT();
  const me = useMe();
  return (
    <>
      <PageHeader title={t('inbox.title')} lead={t('inbox.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      {me.plans.apply ? <InboxWithPlan /> : (
        <div className="card upsell">
          <span className="tile tile--teal"><Icon.inbox /></span>
          <div>
            <h2 className="upsell__title">{t('inbox.upsellTitle')}</h2>
            <p>{t('inbox.upsellText')}</p>
            <Link to={WEB_ROUTES.plans} className="btn btn--primary btn--small">{t('inbox.seePlans')}</Link>
          </div>
        </div>
      )}
    </>
  );
}

function InboxWithPlan() {
  const { t } = useT();
  /* the address call is idempotent: it returns the existing address or makes one */
  const address = useLoad(() => api.post<{ address: string }>('/api/inbox/address').then(r => r.address), []);
  const inbox = useLoad(s => api.get<InboxResponse>('/api/inbox', { signal: s }), []);
  const apps = useLoad(s => api.get<{ applications: Application[] }>('/api/applications', { signal: s }).then(r => r.applications).catch(() => []), []);
  const valueRef = useRef<HTMLParagraphElement>(null);
  const schools = new Map((apps.data ?? []).map(a => [a.school_id, a.school_name]));

  return (
    <>
      <Section title={t('inbox.address')} id="address">
        <div className="card">
          {address.status === 'loading' ? <Loading /> : address.status === 'error' ? <LoadError message={address.message} retry={address.reload} /> : (
            <div className="copyfield">
              <p className="copyfield__value" ref={valueRef} dir="ltr" id="inbox-address">{address.data}</p>
              <CopyButton text={address.data} label={t('inbox.copy')} selectRef={valueRef} />
            </div>
          )}
        </div>
      </Section>
      <Section title={t('inbox.howTitle')} id="how">
        <ul className="ticks">
          <li>{t('inbox.how1')}</li>
          <li>{t('inbox.how2')}</li>
          <li>{t('inbox.how3')}</li>
        </ul>
      </Section>
      <Section title={t('inbox.messages')} id="messages">
        {inbox.status === 'loading' ? <Loading /> : inbox.status === 'error' ? <LoadError message={inbox.message} retry={inbox.reload} /> :
          inbox.data.events.length === 0 ? <p className="empty">{t('inbox.empty')}</p> : (
            <ul className="events">
              {[...inbox.data.events].sort((a, b) => Date.parse(b.received_at) - Date.parse(a.received_at)).map(ev => (
                <li key={ev.id}><EventCard ev={ev} school={ev.school_id ? schools.get(ev.school_id) : undefined} /></li>
              ))}
            </ul>
          )}
      </Section>
    </>
  );
}

function EventCard({ ev, school }: { ev: InboxEvent; school?: string }) {
  const { t, lang } = useT();
  const handle = useErrorHandler();
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState<{ status: 'idle' | 'loading' | 'gone' | 'error'; data?: RawEmail; message?: string }>({ status: 'idle' });
  const canView = ev.raw_available;   // kept while the pass lasts, and at least 30 days (the server decides)
  const links = ev.links.map(l => ({ href: safeHttpUrl(l), text: l })).filter((l): l is { href: string; text: string } => !!l.href);
  const panelId = `raw-${ev.id}`;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || raw.data || raw.status === 'loading') return;
    setRaw({ status: 'loading' });
    try {
      const data = await api.get<RawEmail>(`/api/inbox/raw/${encodeURIComponent(ev.message_id)}`);
      setRaw({ status: 'idle', data });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'not_found') { setRaw({ status: 'gone' }); return; }
      const msg = handle(e);
      setRaw({ status: 'error', message: msg ?? undefined });
    }
  };

  return (
    <article className="event" aria-labelledby={`ev-${ev.id}`}>
      <div className="event__meta">
        <Chip tone="teal">{t(`cat.${ev.category}`)}</Chip>
        {ev.decision && <Chip tone={DECISION_TONE[ev.decision]}>{t(`decision.${ev.decision}`)}</Chip>}
        <time className="event__time" dateTime={ev.received_at}>{t('inbox.received', { date: formatDate(ev.received_at, lang) })}</time>
      </div>
      <h3 className="event__title" id={`ev-${ev.id}`}><bdi>{school ?? t(`cat.${ev.category}`)}</bdi></h3>
      <p className="event__summary" dir="auto">{ev.summary}</p>
      {ev.dates.length > 0 && (
        <div className="event__block">
          <h4>{t('inbox.dates')}</h4>
          <ul>{ev.dates.map(d => <li key={d}><time dateTime={d}>{formatDate(d, lang)}</time></li>)}</ul>
        </div>
      )}
      {ev.actions.length > 0 && (
        <div className="event__block">
          <h4>{t('inbox.actions')}</h4>
          <ul>{ev.actions.map((a, i) => <li key={i} dir="auto">{a}</li>)}</ul>
        </div>
      )}
      {links.length > 0 && (
        <div className="event__block">
          <h4>{t('inbox.links')}</h4>
          <ul>{links.map(l => <li key={l.href}><ExtLink href={l.href} className="break"><bdi dir="ltr">{l.text}</bdi></ExtLink></li>)}</ul>
        </div>
      )}
      {canView ? (
        <div className="event__raw">
          <Button variant="plain" small aria-expanded={open} aria-controls={panelId} onClick={() => void toggle()}>
            {open ? t('inbox.hideFull') : t('inbox.viewFull')}
          </Button>
          <div id={panelId} hidden={!open} className="raw">
            {raw.status === 'loading' && <Loading />}
            {raw.status === 'gone' && <p className="muted">{t('inbox.fullGone')}</p>}
            {raw.status === 'error' && raw.message && <p className="text-danger">{raw.message}</p>}
            {raw.data && (
              <>
                <dl className="raw__head">
                  <div><dt>{t('inbox.from')}</dt><dd dir="ltr"><bdi>{raw.data.from}</bdi></dd></div>
                  <div><dt>{t('inbox.subject')}</dt><dd dir="auto">{raw.data.subject}</dd></div>
                </dl>
                <pre className="raw__body" dir="auto">{raw.data.body_text}</pre>
                <p className="hint">{t('inbox.fullExpires', { date: formatDateTime(raw.data.expires_at, lang) })}</p>
              </>
            )}
          </div>
        </div>
      ) : <p className="hint">{t('inbox.fullGone')}</p>}
    </article>
  );
}
